<template>
  <div class="service-detail">
    <el-card v-loading="loading">
      <!-- 机构头部 -->
      <div class="service-header">
        <div class="service-logo">
          <el-image :src="service.logo || '/static/images/default-org.png'" fit="cover">
            <template #error>
              <div class="image-slot">
                <el-icon>
                  <OfficeBuilding />
                </el-icon>
              </div>
            </template>
          </el-image>
        </div>

        <div class="service-basic">
          <h1>{{ service.name }}</h1>
          <el-tag v-if="service.is_active" type="success">已激活</el-tag>
        </div>
      </div>

      <!-- 机构信息 -->
      <el-descriptions :column="2" border class="service-info">
        <el-descriptions-item label="联系电话">
          <el-icon>
            <Phone />
          </el-icon>
          {{ service.phone }}
        </el-descriptions-item>

        <el-descriptions-item label="电子邮箱">
          <el-icon>
            <Message />
          </el-icon>
          {{ service.email }}
        </el-descriptions-item>

        <el-descriptions-item label="机构地址" :span="2">
          <el-icon>
            <Location />
          </el-icon>
          {{ service.address }}
        </el-descriptions-item>

        <el-descriptions-item label="营业时间" :span="2">
          <el-icon>
            <Clock />
          </el-icon>
          {{ service.business_hours }}
        </el-descriptions-item>
      </el-descriptions>

      <!-- 机构介绍 -->
      <div class="service-section">
        <h2>机构介绍</h2>
        <div class="section-content" v-html="service.description"></div>
      </div>

      <!-- 服务项目 -->
      <div class="service-section">
        <h2>服务项目</h2>
        <el-row :gutter="20">
          <el-col v-for="(item, index) in service.service_items" :key="index" :xs="24" :sm="12" :md="8">
            <el-card class="service-item-card" shadow="hover">
              <h3>{{ item.name }}</h3>
              <p>{{ item.description }}</p>
              <div class="item-price" v-if="item.price">
                <span>价格：</span>
                <span class="price">¥{{ item.price }}</span>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <!-- 联系方式 -->
      <div class="service-section">
        <h2>联系我们</h2>
        <el-alert type="info" :closable="false" show-icon>
          <template #title>
            <div class="contact-info">
              <p><strong>电话：</strong>{{ service.phone }}</p>
              <p><strong>邮箱：</strong>{{ service.email }}</p>
              <p><strong>地址：</strong>{{ service.address }}</p>
            </div>
          </template>
        </el-alert>
      </div>
    </el-card>
  </div>
</template>

<script>
import serviceApi from '@/api/service'
import {
  OfficeBuilding,
  Phone,
  Message,
  Location,
  Clock
} from '@element-plus/icons-vue'

export default {
  name: 'ServiceDetail',

  components: {
    OfficeBuilding,
    Phone,
    Message,
    Location,
    Clock
  },

  data() {
    return {
      service: {
        service_items: []
      },
      loading: false
    }
  },

  mounted() {
    this.loadServiceDetail()
  },

  methods: {
    /**
     * 加载机构详情
     */
    async loadServiceDetail() {
      const serviceId = this.$route.params.id

      if (!serviceId) {
        this.$message.error('机构ID不存在')
        this.$router.back()
        return
      }

      this.loading = true

      try {
        const res = await serviceApi.getServiceDetail(serviceId)

        if (res && res.code === 2000) {
          this.service = res.data
        }
      } catch (error) {
        console.error('加载机构详情失败:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.service-detail {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;

  .el-card {
    max-width: 1400px;
    margin: 0 auto;
  }

  .service-header {
    display: flex;
    align-items: center;
    gap: 30px;
    padding: 20px 0;
    border-bottom: 1px solid #e4e7ed;
    margin-bottom: 30px;

    .service-logo {
      width: 150px;
      height: 150px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;

      .el-image {
        width: 100%;
        height: 100%;
      }

      .image-slot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background-color: #f5f7fa;
        color: #909399;
        font-size: 50px;
      }
    }

    .service-basic {
      flex: 1;

      h1 {
        margin: 0 0 15px 0;
        font-size: 28px;
        color: #303133;
      }
    }
  }

  .service-info {
    margin-bottom: 30px;

    .el-icon {
      margin-right: 5px;
      color: #409eff;
    }
  }

  .service-section {
    margin-top: 30px;

    h2 {
      margin: 0 0 20px 0;
      font-size: 20px;
      color: #303133;
      padding-bottom: 10px;
      border-bottom: 2px solid #409eff;
    }

    .section-content {
      line-height: 1.8;
      color: #606266;
    }

    .service-item-card {
      margin-bottom: 20px;

      h3 {
        margin: 0 0 10px 0;
        font-size: 16px;
        color: #303133;
      }

      p {
        margin: 0 0 10px 0;
        color: #606266;
        font-size: 14px;
        line-height: 1.6;
      }

      .item-price {
        display: flex;
        align-items: center;
        gap: 10px;

        .price {
          font-size: 20px;
          font-weight: bold;
          color: #f56c6c;
        }
      }
    }

    .contact-info {
      p {
        margin: 5px 0;
        line-height: 1.8;
      }
    }
  }
}

@media (max-width: 768px) {
  .service-detail {
    padding: 10px;

    .service-header {
      flex-direction: column;
      text-align: center;

      .service-logo {
        width: 120px;
        height: 120px;
      }

      .service-basic {
        h1 {
          font-size: 22px;
        }
      }
    }
  }
}
</style>
